import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ResultRendererProps } from './types';

type Row = { domain: string; status: 'available' | 'taken' | string; exp_date: string };

function normalize(results: any[]): Row[] {
  if (!Array.isArray(results)) return [];
  const seen = new Set<string>();
  const out: Row[] = [];
  for (const it of results) {
    const domain = String(it?.domain ?? '').trim();
    const status = String(it?.status ?? '').trim().toLowerCase(); // normalizamos
    const exp   = String(it?.exp_date ?? '').trim();
    if (!domain) continue;
    const key = `${domain}::${status}::${exp}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ domain, status: status as any, exp_date: exp });
  }
  return out;
}

const Table: React.FC<{ title: string; rows: Row[]; kind: 'available' | 'taken' }> = ({ title, rows, kind }) => {
  const { t } = useTranslation();

  return (
    <div className="rounded-lg border border-slate-200 overflow-auto">
      <div className="px-3 py-2 text-sm font-semibold text-slate-800 bg-gradient-to-r from-sky-50 to-indigo-50">
        {title} <span className="text-slate-500 font-normal">({rows.length})</span>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-white">
          <tr className="text-slate-600">
            <th className="px-3 py-2 text-left uppercase tracking-wide text-[11px]">
              {t('tools.results_scan.tldhunt.domain_col')}
            </th>
            <th className="px-3 py-2 text-center uppercase tracking-wide text-[11px]">
              {t('tools.results_scan.tldhunt.status_col')}
            </th>
            <th className="px-3 py-2 text-center uppercase tracking-wide text-[11px]">
              {t('tools.results_scan.tldhunt.expiry_col')}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200">
          {rows.map((r, i) => {
            const isAvailable = r.status === 'available';
            const badgeClass = isAvailable
              ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
              : 'bg-rose-50 text-rose-700 ring-rose-200'; // 🔴 Registrado en rojo
            const statusLabel = isAvailable
              ? t('tools.results_scan.tldhunt.status_available')
              : t('tools.results_scan.tldhunt.status_taken');

            return (
              <tr key={`${r.domain}-${i}`} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                <td className="px-3 py-2 font-medium text-slate-900 break-all">{r.domain}</td>
                <td className="px-3 py-2 text-center">
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ${badgeClass}`}>
                    {statusLabel}
                  </span>
                </td>
                <td className="px-3 py-2 text-center text-slate-700">
                  {r.exp_date && r.exp_date !== 'None' ? r.exp_date : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const TLDHuntResults: React.FC<ResultRendererProps> = ({ results }) => {
  const { t } = useTranslation();
  const rows = normalize(results);

  if (rows.length === 0) {
    return <p className="text-sm text-slate-500">{t('tools.results_scan.tldhunt.empty')}</p>;
  }

  const avail = rows.filter(r => r.status === 'available');
  const taken = rows.filter(r => r.status === 'taken');

  return (
    <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Table title={t('tools.results_scan.tldhunt.available_title')} rows={avail} kind="available" />
      <Table title={t('tools.results_scan.tldhunt.taken_title')} rows={taken} kind="taken" />
    </div>
  );
};

export default TLDHuntResults;
